import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * ページを移ったときの表示位置（画面には何も描かない）。
 *
 * ブラウザの普通のページ遷移と違い、SPA は行き先を描くだけでスクロール位置を動かさない。
 * そのため、長いページの下の方（フッターのリンクなど）から移ると、**次のページが途中から出る**。
 * ヘルプの下から「利用規約」を押すと、規約の §7 あたりが開いていた。
 *
 * 決めごと:
 * - 行き先に `#見出し` が付いていれば、そこへ送る（`/help#ads` など。SPA は自分で送らないと効かない）。
 *   見つからなければ先頭へ。
 * - **戻る・進む（POP）では動かさない。** 読んでいた位置はブラウザが覚えているので、そちらに任せる。
 * - 同じパスのまま `?` だけが変わるとき（`/shindan?theme=`、結果ページの相性の `?with=` `&rel=` など）は動かさない。同じページの続きだから。
 *   `setSearchParams` は `#見出し` を落とすので、同じパスで `#` が消えただけのときも動かさない。
 *   遷移の種類（PUSH・REPLACE）が変わっただけで先頭へ戻すと、相性を比べるたび・関係を選ぶたびに上まで飛んでいた。
 * - 動かすのは一瞬で（なめらかに動かさない）。下から上へ長い距離を流すと、何が起きたか分からなくなる。
 *
 * `useLayoutEffect` なのは、描く前に位置を決めるため（`useEffect` だと前の位置で 1 コマ描いてちらつく）。
 */
/** `#見出し` へ送るとき、位置が落ち着くまで合わせ直す時間。 */
const SETTLE_MS = 800;
/** 読み手が自分で動かしたら手を引く。そのきっかけ。 */
const USER_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

export function ScrollOnNavigate() {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();
  // 最初の 1 回（＝この URL を直接開いたとき）か。戻る・進むと見分けるために持つ
  const opening = useRef(true);
  // 1 つ前のパス。`?` だけの遷移と見分けるために持つ
  const previousPath = useRef(pathname);

  useLayoutEffect(() => {
    const firstRun = opening.current;
    opening.current = false;
    const samePath = pathname === previousPath.current;
    previousPath.current = pathname;

    // 戻る・進む（POP）はブラウザが位置を覚えているので任せる。
    // ただし **直接開いた 1 回目も POP** で来るので、そこは除く。
    if (navigationType === 'POP' && !firstRun) return;
    // 同じページのまま `?` だけ変わった（`#` が落ちただけのときも）。読んでいる位置のままにする
    if (!firstRun && samePath && !hash) return;

    const target = hash ? findTarget(hash) : null;
    if (!target) {
      // 直接開いたときの位置はブラウザに任せる（再読み込みで読んでいた位置に戻るのを壊さない）
      if (!firstRun) window.scrollTo(0, 0);
      return;
    }

    /**
     * 1 回送っただけでは足りない。**直接 URL を開いたとき**は、この時点でまだスタイルシートが
     * 届いておらず、字の折り返しが決まっていないことがある。文章が短いぶん行き先が上にずれていて、
     * 手前で止まる（本番で §7 の 442px 手前に着地していた）。あとから本来の位置に伸びるので、
     * **位置が動かなくなるまで合わせ直す**。画面内の遷移なら 2 コマで終わる。
     */
    let stopped = false;
    const until = Date.now() + SETTLE_MS;
    let previousTop = Number.NaN;

    const align = () => {
      if (stopped) return;
      target.scrollIntoView();
      const top = Math.round(target.getBoundingClientRect().top);
      // まだ動いている、または読み込みが終わっていないあいだは見張る
      if ((top !== previousTop || document.readyState !== 'complete') && Date.now() < until) {
        previousTop = top;
        requestAnimationFrame(align);
      }
    };
    align();

    // 読み手が自分で動かしたら、そこから先は邪魔しない
    const cancel = () => { stopped = true; };
    for (const type of USER_EVENTS) window.addEventListener(type, cancel, { passive: true });

    return () => {
      stopped = true;
      for (const type of USER_EVENTS) window.removeEventListener(type, cancel);
    };
    // 遷移ごとに 1 回（`key` は遷移のたびに変わる）。パス・`#` で決めると `?` だけの遷移と見分けられない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return null;
}

/** `#見出し` から送り先を引く。ヘルプの目次は日本語の ID なので、戻したものと生のものの両方で引く。 */
function findTarget(hash: string): HTMLElement | null {
  const raw = hash.slice(1);
  if (!raw) return null;
  let decoded = raw;
  try { decoded = decodeURIComponent(raw); } catch { /* 壊れていれば生のまま引く */ }
  return document.getElementById(decoded) ?? document.getElementById(raw);
}
